/**
 * Account-scoped OAuth session storage. Lets different browser tabs run
 * different accounts simultaneously (each opened via its own /validate?code=
 * link) without one tab's login overwriting another's — the previous
 * design used a single flat `agentic_ai_auth` key shared by the whole
 * origin, so the second account to log in silently took over every tab.
 *
 * The fix: namespace the OAuth credential/settings keys by account id in
 * localStorage (shared across tabs, but now one entry per account), and
 * track which account THIS tab is bound to in sessionStorage (inherently
 * per-tab — never shared with other tabs, even of the same origin).
 */

const ACTIVE_ACCOUNT_SESSION_KEY = 'active_account_id';
// localStorage (shared across tabs), not sessionStorage: this is the
// fallback a brand-new tab uses when it can't tell which account to bind to
// on its own — "whichever account was logged into most recently", so a
// right-click "open in new tab" keeps working for the common single-account
// case even with stale entries from old test accounts still in storage.
const LAST_ACTIVE_ACCOUNT_KEY = 'last_active_account_id';
const OAUTH_KEY_PREFIX = 'agentic_ai_auth';
const SETTINGS_KEY_PREFIX = 'App-Settings';
const API_KEY_PREFIX = 'ai_tech_api_key';

// Sentinel for a pre-existing, non-namespaced session from before this
// scoping was introduced — keeps old sessions working without a migration.
const LEGACY = '__legacy__';

// Generic account-scoped key builder for any localStorage/sessionStorage
// base key — same `${base}::${accountId}` convention as oauthKey/settingsKey,
// so every per-account cache/preference in the app can share one namespacing
// rule instead of each page inventing its own suffix format.
function scopedKey(baseKey, accountId) {
  return accountId && accountId !== LEGACY ? `${baseKey}::${accountId}` : baseKey;
}

function oauthKey(accountId) {
  return scopedKey(OAUTH_KEY_PREFIX, accountId);
}

function settingsKey(accountId) {
  return scopedKey(SETTINGS_KEY_PREFIX, accountId);
}

function apiKeyStorageKey(accountId) {
  return scopedKey(API_KEY_PREFIX, accountId);
}

// Scans both the OAuth and the plain-API-key namespaces: a tab could have
// landed on either login flow, and both bind the same active-account-id
// session key, so either can tell us which accounts are known locally.
function listNamespacedAccountIds() {
  const prefixes = [`${OAUTH_KEY_PREFIX}::`, `${API_KEY_PREFIX}::`];
  const ids = new Set();
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k) continue;
    for (const prefix of prefixes) {
      if (k.startsWith(prefix)) ids.add(k.slice(prefix.length));
    }
  }
  return Array.from(ids);
}

/**
 * Which account this tab is bound to for the OAuth flow, if any.
 * - If this tab already bound itself (via bindActiveAccountId), use that.
 * - If exactly one namespaced account exists in localStorage, bind to it —
 *   the unambiguous case.
 * - Otherwise (zero, or 2+ accounts incl. stale leftovers from old test
 *   accounts), fall back to whichever account was logged into most
 *   recently, so a plain new tab still works for the common
 *   single-account-at-a-time case instead of landing on /login.
 * - Falls back to the legacy flat key so pre-existing sessions still work.
 */
function getActiveAccountId() {
  const bound = sessionStorage.getItem(ACTIVE_ACCOUNT_SESSION_KEY);
  if (bound) return bound;

  const ids = listNamespacedAccountIds();
  if (ids.length === 1) {
    sessionStorage.setItem(ACTIVE_ACCOUNT_SESSION_KEY, ids[0]);
    return ids[0];
  }

  const lastActive = localStorage.getItem(LAST_ACTIVE_ACCOUNT_KEY);
  if (lastActive && ids.includes(lastActive)) {
    sessionStorage.setItem(ACTIVE_ACCOUNT_SESSION_KEY, lastActive);
    return lastActive;
  }

  if (
    ids.length === 0
    && (localStorage.getItem(OAUTH_KEY_PREFIX) || localStorage.getItem(API_KEY_PREFIX))
  ) {
    return LEGACY;
  }
  return null;
}

function bindActiveAccountId(accountId) {
  if (!accountId) return;
  const id = String(accountId);
  sessionStorage.setItem(ACTIVE_ACCOUNT_SESSION_KEY, id);
  localStorage.setItem(LAST_ACTIVE_ACCOUNT_KEY, id);
}

function clearActiveAccountId() {
  sessionStorage.removeItem(ACTIVE_ACCOUNT_SESSION_KEY);
}

// Reads this tab's active account's plain API key, falling back to the
// legacy flat key for sessions written before this key was account-scoped.
function readStoredApiKey() {
  const accountId = getActiveAccountId();
  if (accountId) {
    const scoped = localStorage.getItem(apiKeyStorageKey(accountId));
    if (scoped) return scoped;
  }
  return localStorage.getItem(API_KEY_PREFIX);
}

export {
  LEGACY,
  scopedKey,
  oauthKey,
  settingsKey,
  apiKeyStorageKey,
  readStoredApiKey,
  listNamespacedAccountIds,
  getActiveAccountId,
  bindActiveAccountId,
  clearActiveAccountId,
};
